/* eslint-disable @typescript-eslint/no-unused-vars */
import { computed, type Ref } from 'vue'
import { useRoute, useRouter, type RouteLocationNormalizedLoaded, type Router } from 'vue-router'
import { useCustomerStore } from '@/stores/customer'
import { storeToRefs } from 'pinia'
import type { Contract, ContractDetail, InsuranceCaseDetail } from '@/types/customer'

export interface BreadcrumbItem {
  label: string
  path: string
  loading?: boolean
  icon?: string
  shortLabel?: string
}

function addHomeBreadcrumb(
  items: BreadcrumbItem[],
  router: Router,
  clientId: string | number,
): void {
  /* const resolved = router.resolve({
    name: 'home',
  })
  items.push({
    label: 'Dashboard',
    path: resolved.path,
    icon: 'House',
    shortLabel: 'Home',
  }) */
}

function addContractsBreadcrumb(
  items: BreadcrumbItem[],
  router: Router,
  clientId: string | number,
): void {
  /* const resolved = router.resolve({
    name: 'contracts',
    params: { clientId: clientId.toString() },
  })
  items.push({
    label: 'Smlouvy',
    path: resolved.path,
    icon: 'Document',
    shortLabel: 'Smlouvy',
  }) */
}

function addContractDetailBreadcrumb(
  items: BreadcrumbItem[],
  router: Router,
  clientId: string | number,
  contractDetail: Ref<ContractDetail | null>,
): void {
  const contract = contractDetail.value
  if (!contract) {
    items.push({
      label: 'Detail smlouvy',
      path: '#',
      icon: 'Document',
      shortLabel: 'Smlouva',
      loading: true,
    })
    return
  }

  const resolved = router.resolve({
    name: 'contract-detail',
    params: {
      clientId: clientId.toString(),
      contractId: contract.id.toString(),
    },
  })

  items.push({
    label: `Smlouva ${contract.contractNumber}`,
    path: resolved.path,
    icon: 'Document',
    shortLabel: contract.contractNumber,
  })
}

function addSubcontractDetailBreadcrumb(
  items: BreadcrumbItem[],
  router: Router,
  clientId: string | number,
  subcontractDetail: Ref<ContractDetail | null>,
  contractDetail: Ref<ContractDetail | null>,
): void {
  const subcontract = subcontractDetail.value
  if (!subcontract) {
    items.push({
      label: 'Podsmlouva',
      path: '#',
      icon: 'Document',
      shortLabel: 'Podsmlouva',
      loading: true,
    })
    return
  }

  // Add parent contract breadcrumb if available
  if (contractDetail.value) {
    addContractDetailBreadcrumb(items, router, clientId, contractDetail)
  }

  const resolved = router.resolve({
    name: 'subcontract-detail',
    params: {
      clientId: clientId.toString(),
      subcontractId: subcontract.id.toString(),
    },
  })

  items.push({
    label: `Podsmlouva ${subcontract.contractNumber}`,
    path: resolved.path,
    icon: 'Document',
    shortLabel: subcontract.contractNumber,
  })
}

function addInsuranceCaseBreadcrumb(
  items: BreadcrumbItem[],
  router: Router,
  clientId: string | number,
  insuranceCaseDetail: Ref<InsuranceCaseDetail | null>,
  contractDetail: Ref<ContractDetail | null>,
  subContractDetail: Ref<ContractDetail | null>,
): void {
  const insuranceCase = insuranceCaseDetail.value
  if (!insuranceCase) {
    items.push({
      label: 'Pojistná událost',
      path: '#',
      icon: 'Money',
      loading: true,
    })
    return
  }

  // Add parent breadcrumbs if available
  if (insuranceCase.subcontractId && subContractDetail.value) {
    addSubcontractDetailBreadcrumb(items, router, clientId, subContractDetail, contractDetail)
  } else if (insuranceCase.contractId && contractDetail.value) {
    addContractDetailBreadcrumb(items, router, clientId, contractDetail)
  }

  const resolved = router.resolve({
    name: 'insurance-case-detail',
    params: {
      clientId: clientId.toString(),
      caseId: insuranceCase.id.toString(),
    },
  })

  items.push({
    label: `Pojistná událost ${insuranceCase.caseNumber || ''}`,
    path: resolved.path,
    icon: 'Money',
    shortLabel: insuranceCase.caseNumber as string,
  })
}

export function useBreadcrumbs() {
  const route = useRoute()
  const router = useRouter()
  const customerStore = useCustomerStore()

  const { contracts, subcontracts, contractDetail, subContractDetail, insuranceCaseDetail } =
    storeToRefs(customerStore)

  const breadcrumbs = computed<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = []
    const clientId = route.params.clientId as string | number

    addHomeBreadcrumb(items, router, clientId)

    switch (route.name) {
      case 'home':
        // Only show home for dashboard
        break

      case 'contracts':
        addContractsBreadcrumb(items, router, clientId)
        break

      case 'contract-detail':
        addContractsBreadcrumb(items, router, clientId)
        addContractDetailBreadcrumb(items, router, clientId, contractDetail)
        break

      case 'subcontract-detail':
        addContractsBreadcrumb(items, router, clientId)
        addSubcontractDetailBreadcrumb(items, router, clientId, subContractDetail, contractDetail)
        break

      case 'insurance-case-detail':
        addContractsBreadcrumb(items, router, clientId)
        addInsuranceCaseBreadcrumb(
          items,
          router,
          clientId,
          insuranceCaseDetail,
          contractDetail,
          subContractDetail,
        )
        break

      case 'new-message':
      case 'new-message-contract':
      case 'new-message-subcontract':
      case 'new-message-insurance-case': {
        const resolved = router.resolve({
          name: route.name,
          params: route.params,
        })
        items.push({
          label: 'Nová zpráva',
          path: resolved.path,
          icon: 'Message',
          shortLabel: 'Nová zpráva',
        })
        break
      }

      default:
      /* if (route.meta?.title) {
          const resolved = router.resolve({
            name: route.name,
            params: route.params,
            query: route.query,
          })
          items.push({
            label: route.meta.title as string,
            path: resolved.path,
          })
        } */
    }

    return items
  })

  return {
    breadcrumbs,
  }
}
